import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import {
  Box,
  chakra,
  createListCollection,
  Field,
  HStack,
  Link,
  NativeSelect,
  Select,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  Button as ButtonComponent,
  InputField,
  SectionCard,
} from '@themes/aegix';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HiInformationCircle, HiRefresh } from 'react-icons/hi';
import { HiTrash } from 'react-icons/hi2';
import { Link as RouterLink } from 'react-router-dom';
import { DrawerComponent } from '../../ui/drawerComponent';
import { toaster } from '../../ui/toaster';
import {
  createScimConfig,
  deleteScimConfig,
  getScimConfig,
  getScimSyncStatus,
  listLinkableSamlConfigs,
  type ScimConfigDetail,
  type ScimConfigWrite,
  type ScimIdpType,
  setScimConfigEnabled,
  triggerScimSync,
  updateScimConfig,
  uploadGoogleScimCreds,
} from '../../../services';
import { appLabel, deriveAppNameFromHost } from './appName';
import { BearerRevealModal } from './BearerRevealModal';
import { BearerRotateModal } from './BearerRotateModal';
import { CopyableRow } from './CopyableRow';
import { credentialShapeFor } from './credentialShapes';
import { CredentialInput } from './CredentialInput';
import { GoogleCredsImportDialog } from './GoogleCredsImportDialog';
import { StatusPanel } from './StatusPanel';

const IDP_TYPE_CHOICES: { value: ScimIdpType; label: string }[] = [
  { value: 'okta', label: 'Okta' },
  { value: 'azure', label: 'Microsoft Entra ID' },
  { value: 'google', label: 'Google Workspace' },
  { value: 'duo', label: 'Duo' },
];

const PULL_IDP_TYPES: ScimIdpType[] = ['google', 'duo'];
const PUSH_IDP_TYPES: ScimIdpType[] = ['okta', 'azure'];

function isPullIdp(t: ScimIdpType | undefined): boolean {
  return !!t && PULL_IDP_TYPES.includes(t);
}
function isPushIdp(t: ScimIdpType | undefined): boolean {
  return !!t && PUSH_IDP_TYPES.includes(t);
}

// Generic SCIM runbook (used when the admin's IdP doesn't have a
// dedicated guide). Linked from the drawer header (i) icon, mirroring
// the SAML drawer's GENERIC_SAML_RUNBOOK_URL pattern. Served by the
// in-app admin-docs library; gated by
// `accounts.view_admin_guides`.
const GENERIC_SCIM_RUNBOOK_URL = '/account/docs/scim-generic-integration-setup';

interface FormState {
  idp_name: string;
  idp_type: ScimIdpType;
  app_name: string;
  enabled: boolean;
  saml_config: number | null;
  polling_enabled: boolean;
  polling_interval_minutes: number;
  rawIdpCredentialsText: string;
  rawWebhookSecret: string;
  // Google-only. `adminEmail` is the super-admin we impersonate when
  // calling Admin SDK Directory — required by domain-wide delegation.
  // Lives in the drawer so the import dialog stays focused on the
  // credentials file itself (which does NOT contain admin_email).
  // `pendingGoogleFile` is staged in create mode: the upload endpoint
  // needs a configId, so we POST the SCIM row first, then upload.
  adminEmail: string;
  pendingGoogleFile: File | null;
}

function initialFormState(): FormState {
  const host = typeof window !== 'undefined' ? window.location.host : '';
  return {
    idp_name: '',
    idp_type: 'okta',
    app_name: deriveAppNameFromHost(host),
    enabled: true,
    saml_config: null,
    // polling_enabled is implicit: any enabled pull IdP polls. The UI no
    // longer surfaces a separate toggle (per UX simplification), so we
    // always send true; the backend ignores it for push IdPs.
    polling_enabled: true,
    polling_interval_minutes: 5,
    rawIdpCredentialsText: '',
    rawWebhookSecret: '',
    adminEmail: '',
    pendingGoogleFile: null,
  };
}

function hydrateFormFromDetail(detail: ScimConfigDetail): FormState {
  return {
    idp_name: detail.idp_name,
    idp_type: detail.idp_type,
    app_name: detail.app_name,
    enabled: detail.enabled,
    saml_config: detail.saml_config,
    // Always true; see comment on initialFormState. Existing rows with
    // polling_enabled=false get promoted to true on next save.
    polling_enabled: true,
    polling_interval_minutes: detail.polling_interval_minutes || 5,
    rawIdpCredentialsText: '',
    rawWebhookSecret: '',
    // admin_email is exposed on detail (it's the public super-admin
    // email, not a secret), so hydrate it directly. Lets the drawer
    // persist the value across edits without a re-import.
    adminEmail: detail.admin_email ?? '',
    pendingGoogleFile: null,
  };
}

function buildWritePayload(
  state: FormState,
  isCreate: boolean,
): ScimConfigWrite {
  const payload: ScimConfigWrite = {
    idp_name: state.idp_name,
    idp_type: state.idp_type,
    app_name: state.app_name,
    saml_config: state.saml_config,
    polling_enabled: state.polling_enabled,
    polling_interval_minutes: state.polling_interval_minutes,
  };
  // Send `enabled` only on create — the footer Disable/Enable button owns
  // this field on edit, and including it on Save would let stale form
  // state clobber a recent footer toggle.
  if (isCreate) payload.enabled = state.enabled;
  // Role/site mappings are owned by the linked SAMLConfig now.
  // Don't send role_mappings/school_mappings on SCIM writes.
  // Bearer token: never sent from the form — backend auto-generates on
  // create (revealed via BearerRevealModal) and BearerRotateModal owns
  // refresh on edit.
  // Google admin_email merges into idp_credentials without clobbering
  // the rest of the dict. Sent on every save when the field is filled
  // so the value persists even without re-importing the JSON.
  if (state.idp_type === 'google' && state.adminEmail.trim()) {
    payload.update_admin_email = state.adminEmail.trim();
  }
  if (state.rawWebhookSecret)
    payload.raw_webhook_secret = state.rawWebhookSecret;
  if (state.rawIdpCredentialsText.trim()) {
    try {
      payload.raw_idp_credentials = JSON.parse(state.rawIdpCredentialsText);
    } catch {
      // Surfaced as a validation error by the CredentialInput preview.
    }
  }
  return payload;
}

export interface ScimConfigDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  configId: number | null;
}

export function ScimConfigDrawer({
  isOpen,
  onClose,
  configId,
}: ScimConfigDrawerProps) {
  const { t } = useTranslation();
  const isCreate = configId === null;
  const queryClient = useQueryClient();

  const detailQuery = useQuery({
    queryKey: ['sso', 'scim-configs', configId],
    queryFn: () => getScimConfig(configId as number),
    enabled: isOpen && !isCreate && configId !== null,
  });

  const samlConfigsQuery = useQuery({
    queryKey: ['sso', 'saml-configs'],
    queryFn: listLinkableSamlConfigs,
    enabled: isOpen,
  });
  const statusQuery = useQuery({
    queryKey: ['sso', 'scim-configs', configId, 'status'],
    queryFn: () => getScimSyncStatus(configId as number),
    enabled: isOpen && !isCreate && configId !== null,
    refetchInterval: 15_000,
  });

  const [form, setForm] = useState<FormState>(() => initialFormState());
  const [hydratedFor, setHydratedFor] = useState<number | null | 'create'>(
    null,
  );
  const [bearerModalOpen, setBearerModalOpen] = useState(false);
  const [googleImportOpen, setGoogleImportOpen] = useState(false);
  const [revealedBearer, setRevealedBearer] = useState<string | null>(null);

  const methods = useForm<FormState>({
    defaultValues: initialFormState(),
    mode: 'onBlur',
  });
  const {
    register,
    control,
    formState: { errors },
  } = methods;

  useEffect(() => {
    if (!isOpen) return;
    if (isCreate && hydratedFor !== 'create') {
      const init = initialFormState();
      setForm(init);
      setHydratedFor('create');
      methods.reset(init);
      return;
    }
    if (!isCreate && detailQuery.data && hydratedFor !== configId) {
      const detailForm = hydrateFormFromDetail(detailQuery.data);
      setForm(detailForm);
      setHydratedFor(configId);
      methods.reset(detailForm);
    }
  }, [isOpen, isCreate, configId, detailQuery.data, hydratedFor, methods]);

  useEffect(() => {
    if (!isOpen) setHydratedFor(null);
  }, [isOpen]);

  const detail = detailQuery.data;
  const samlLinked = form.saml_config != null;

  const samlCollection = useMemo(
    () =>
      createListCollection({
        items: (samlConfigsQuery.data ?? []).map((c) => ({
          label: `${c.name} (${c.user_hostname_match})`,
          value: String(c.id),
        })),
      }),
    [samlConfigsQuery.data],
  );

  const saveMutation = useMutation({
    mutationFn: async (updatedForm?: FormState) => {
      const currentForm = updatedForm ?? form;
      const payload = buildWritePayload(currentForm, isCreate);
      const saved = isCreate
        ? await createScimConfig(payload)
        : await updateScimConfig(configId as number, payload);
      // Google: if a service-account file is staged (e.g. imported in
      // create mode before the row existed), upload it now that we have
      // an id. admin_email is required by the endpoint.
      if (currentForm.pendingGoogleFile && saved.id && currentForm.adminEmail) {
        await uploadGoogleScimCreds(
          saved.id,
          currentForm.pendingGoogleFile,
          currentForm.adminEmail,
        );
      }
      return saved;
    },
    onSuccess: (saved) => {
      toaster.create({
        title: isCreate
          ? t('sso.syncConfigCreated', 'Sync configuration created')
          : t('common.saved', 'Saved'),
        type: 'success',
        duration: 4000,
      });
      queryClient.invalidateQueries({ queryKey: ['sso', 'scim-configs'] });
      if (saved.generated_bearer_token) {
        // Hold the drawer open behind the reveal modal so the admin can
        // copy the plaintext before navigating away.
        setRevealedBearer(saved.generated_bearer_token);
      } else {
        onClose();
      }
    },
    onError: (err) => {
      toaster.create({
        title: t('sso.saveFailed', 'Save failed'),
        description: (err as Error).message,
        type: 'error',
        duration: 6000,
      });
    },
  });

  const toggleEnabledMutation = useMutation({
    mutationFn: () =>
      setScimConfigEnabled(configId as number, !(detail?.enabled ?? true)),
    onSuccess: (saved) => {
      toaster.create({
        title: saved.enabled
          ? t('common.enabled', 'Enabled')
          : t('common.disabled', 'Disabled'),
        type: 'success',
        duration: 4000,
      });
      queryClient.invalidateQueries({ queryKey: ['sso', 'scim-configs'] });
      queryClient.invalidateQueries({
        queryKey: ['sso', 'scim-configs', configId],
      });
      onClose();
    },
    onError: (err) => {
      toaster.create({
        title: t('sso.updateFailed', 'Update failed'),
        description: (err as Error).message,
        type: 'error',
        duration: 6000,
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteScimConfig(configId as number),
    onSuccess: () => {
      toaster.create({
        title: t('sso.syncConfigDeleted', 'Sync configuration deleted'),
        type: 'success',
        duration: 4000,
      });
      queryClient.invalidateQueries({ queryKey: ['sso', 'scim-configs'] });
      onClose();
    },
    onError: (err) => {
      toaster.create({
        title: t('sso.deleteFailed', 'Delete failed'),
        description: (err as Error).message,
        type: 'error',
        duration: 6000,
      });
    },
  });

  const handleDelete = () => {
    const ok = window.confirm(
      t(
        'sso.deleteSyncConfirmMsg',
        'Delete this Sync configuration "{{name}}"? It will be removed from the admin list. Contact engineering to recover.',
        { name: form.idp_name || '' },
      ),
    );
    if (ok) deleteMutation.mutate();
  };

  const syncMutation = useMutation({
    mutationFn: () => triggerScimSync(configId as number),
    onSuccess: (result) => {
      toaster.create({
        title: t('sso.syncDispatched', 'Sync dispatched'),
        description: result.detail,
        type: 'success',
        duration: 5000,
      });
      queryClient.invalidateQueries({
        queryKey: ['sso', 'scim-configs', configId, 'status'],
      });
    },
    onError: (err) => {
      toaster.create({
        title: t('sso.syncFailedDispatch', 'Sync failed to dispatch'),
        description: (err as Error).message,
        type: 'error',
        duration: 6000,
      });
    },
  });

  const loading = !isCreate && detailQuery.isLoading;
  const savedEnabled = detail?.enabled ?? true;

  // RHF owns only idp_name and saml_config; every other field lives in `form`.
  const onSubmit = (values: FormState) => {
    saveMutation.mutate({
      ...form,
      idp_name: values.idp_name,
      saml_config: values.saml_config,
    });
  };
  return (
    <>
      <DrawerComponent
        isOpen={isOpen}
        onClose={onClose}
        title={isCreate ? t('sso.addSync', 'Add Sync') : t('sso.editSync', 'Edit Sync')}
        titleAdornment={
          <Link
            asChild
            aria-label="Open SCIM setup guide"
            color="text.secondary"
            _hover={{ color: 'text.primary' }}
            display="inline-flex"
            alignItems="center"
            fontSize="lg"
            data-e2e="sso-scim-help-link"
          >
            <RouterLink to={GENERIC_SCIM_RUNBOOK_URL}>
              <HiInformationCircle />
            </RouterLink>
          </Link>
        }
        size="md"
        closeOnOutside={false}
        closeOnEsc={false}
        footer={
          <HStack width="100%" gap={4}>
            {!isCreate && configId !== null && detail ? (
              <>
                <ButtonComponent
                  visual="alert"
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                  icon={<HiTrash />}
                  flex={1}
                  data-e2e="sso-scim-delete"
                >
                  {deleteMutation.isPending
                    ? t('common.deleting', 'Deleting…')
                    : t('common.delete', 'Delete')}
                </ButtonComponent>
                <ButtonComponent
                  visual="secondary"
                  onClick={() => toggleEnabledMutation.mutate()}
                  disabled={toggleEnabledMutation.isPending}
                  flex={1}
                  data-e2e="sso-scim-toggle-enabled"
                >
                  {toggleEnabledMutation.isPending
                    ? savedEnabled
                      ? t('common.disabling', 'Disabling…')
                      : t('common.enabling', 'Enabling…')
                    : savedEnabled
                      ? t('common.disable', 'Disable')
                      : t('common.enable', 'Enable')}
                </ButtonComponent>
                <ButtonComponent
                  visual="secondary"
                  onClick={() => syncMutation.mutate()}
                  disabled={syncMutation.isPending}
                  icon={<HiRefresh />}
                  flex={1}
                  data-e2e="sso-scim-sync"
                >
                  {syncMutation.isPending
                    ? t('sso.syncing', 'Syncing…')
                    : t('sso.sync', 'Sync')}
                </ButtonComponent>
              </>
            ) : null}
            <ButtonComponent visual="secondary" onClick={onClose} flex={1}>
              {t('common.cancel', 'Cancel')}
            </ButtonComponent>
            <ButtonComponent
              visual="primary"
              type="submit"
              disabled={saveMutation.isPending || (isCreate && !samlLinked)}
              flex={1}
              data-e2e="sso-save"
              form="scim-config-form"
            >
              {saveMutation.isPending
                ? t('common.saving', 'Saving…')
                : isCreate
                  ? t('common.create', 'Create')
                  : t('common.save', 'Save')}
            </ButtonComponent>
          </HStack>
        }
      >
        {loading ? (
          <Box py={8}>
            <Spinner /> <Text ml={3}>{t('sso.loadingConfig', 'Loading configuration…')}</Text>
          </Box>
        ) : detailQuery.isError ? (
          <Text color="red.600">
            {t('sso.failedLoadConfig', 'Failed to load configuration:')} {(detailQuery.error as Error).message}
          </Text>
        ) : (
          <FormProvider {...methods}>
            <form
              id="scim-config-form"
              onSubmit={methods.handleSubmit(onSubmit)}
            >
              <VStack align="stretch" gap={4}>
                <SectionCard title={t('sso.application', 'Application')}>
                  <VStack align="stretch" gap={3}>
                    <Field.Root invalid={!!errors.idp_name}>
                      <Field.Label htmlFor="sso-idp-name">{t('common.name', 'Name')}</Field.Label>
                      <InputField
                        id="sso-idp-name"
                        placeholder={t('sso.scimNamePlaceholder', 'e.g. Acme Okta')}
                        data-e2e="sso-idp-name"
                        {...register('idp_name', {
                          required: t('sso.nameRequired', 'Name is required'),
                          // TODO: Retire this setForm shadow state once the rest of the drawer
                          // is migrated to react-hook-form (methods.watch or methods.getValues).
                          onChange: (e) =>
                            setForm((f) => ({
                              ...f,
                              idp_name: e.target.value,
                            })),
                        })}
                        aria-describedby={
                          errors.idp_name ? 'sso-idp-name-error' : undefined
                        }
                      />
                      {errors.idp_name && (
                        <Field.ErrorText
                          id="sso-idp-name-error"
                          fontSize="sm"
                          data-e2e="scim-idp-name-error"
                        >
                          {errors.idp_name.message}
                        </Field.ErrorText>
                      )}
                    </Field.Root>
                    <HStack gap={3}>
                      <Field.Root flex={1}>
                        <Field.Label htmlFor="sso-idp-type">{t('common.type', 'Type')}</Field.Label>
                        <NativeSelect.Root>
                          <NativeSelect.Field
                            id="sso-idp-type"
                            value={form.idp_type}
                            onChange={(e) =>
                              setForm((f) => ({
                                ...f,
                                idp_type: e.target.value as ScimIdpType,
                                // Credential shapes differ per IdP — clear
                                // any in-progress values so stale keys from
                                // the previous type don't leak into the
                                // payload for the new one.
                                rawIdpCredentialsText: '',
                              }))
                            }
                            data-e2e="sso-idp-type"
                          >
                            {IDP_TYPE_CHOICES.map((c) => (
                              <option key={c.value} value={c.value}>
                                {c.label}
                              </option>
                            ))}
                          </NativeSelect.Field>
                          <NativeSelect.Indicator />
                        </NativeSelect.Root>
                      </Field.Root>
                      <Field.Root flex={1}>
                        <Field.Label htmlFor="sso-app-name">{t('sso.target', 'Target')}</Field.Label>
                        <InputField
                          id="sso-app-name"
                          data-e2e="sso-scim-app"
                          value={appLabel(form.app_name)}
                          readOnly
                        />
                      </Field.Root>
                    </HStack>
                  </VStack>
                </SectionCard>

                <SectionCard title={t('sso.linkedSignin', 'Linked Sign-in')}>
                  {/* This Controller manages validation for saml_config because it is a non-registered custom select input */}
                  <Controller
                    name="saml_config"
                    control={control}
                    rules={{
                      validate: (val) => {
                        if (isCreate && val === null) {
                          return t('sso.linkSigninValidation', 'Link a Sign-in configuration to enable SCIM provisioning.');
                        }
                        return true;
                      },
                    }}
                    render={({ field }) => (
                      <Field.Root invalid={!!errors.saml_config}>
                        <Select.Root
                          ids={{ trigger: 'sso-linked-saml' }}
                          collection={samlCollection}
                          value={
                            field.value != null ? [String(field.value)] : ['']
                          }
                          onValueChange={({ value }) => {
                            const v = value[0] ?? '';
                            const numValue = v ? Number(v) : null;
                            field.onChange(numValue);
                            setForm((f) => ({
                              ...f,
                              saml_config: numValue,
                            }));
                          }}
                        >
                          <Select.HiddenSelect aria-label={t('sso.linkedSignin', 'Linked Sign-in configuration')} />
                          <Select.Control>
                            <Select.Trigger aria-label={t('sso.linkedSignin', 'Linked Sign-in configuration')}>
                              <Select.ValueText placeholder={t('sso.selectSigninPlaceholder', 'Select a sign-in…')} />
                            </Select.Trigger>
                          </Select.Control>
                          <Select.Positioner>
                            <Select.Content>
                              {samlCollection.items.map((item) => (
                                <Select.Item
                                  item={item}
                                  key={item.value || 'none'}
                                >
                                  {item.label}
                                </Select.Item>
                              ))}
                            </Select.Content>
                          </Select.Positioner>
                        </Select.Root>
                        {errors.saml_config && (
                          <Field.ErrorText
                            fontSize="sm"
                            data-e2e="scim-saml-link-error"
                          >
                            {errors.saml_config.message}
                          </Field.ErrorText>
                        )}
                      </Field.Root>
                    )}
                  />
                </SectionCard>

                <SectionCard
                  title={t('sso.credentials', 'Credentials')}
                  action={
                    form.idp_type === 'google' ? (
                      <ButtonComponent
                        type="button"
                        visual="secondary"
                        size="sm"
                        onClick={() => setGoogleImportOpen(true)}
                        disabled={!form.adminEmail.includes('@')}
                        data-e2e="sso-google-creds-open"
                      >
                        {t('common.import', 'Import')}
                      </ButtonComponent>
                    ) : !isCreate && isPushIdp(form.idp_type) ? (
                      <ButtonComponent
                        type="button"
                        visual="secondary"
                        size="sm"
                        onClick={() => setBearerModalOpen(true)}
                        data-e2e="sso-bearer-rotate"
                      >
                        {t('sso.rotate', 'Rotate')}
                      </ButtonComponent>
                    ) : undefined
                  }
                >
                  {form.idp_type === 'google' ? (
                    <VStack align="stretch" gap={3}>
                      <Field.Root>
                        <Field.Label htmlFor="sso-google-admin-email">
                          {t('sso.adminEmail', 'Admin Email')}
                        </Field.Label>
                        <InputField
                          id="sso-google-admin-email"
                          type="email"
                          data-e2e="sso-google-admin-email"
                          value={form.adminEmail}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              adminEmail: e.target.value,
                            }))
                          }
                          placeholder="super-admin@yourdomain.com"
                        />
                      </Field.Root>
                      <Text fontSize="sm" color="text.secondary">
                        {form.pendingGoogleFile
                          ? t('sso.serviceAccountStaged', 'Service account staged: {{name}}. It will upload when you save.', { name: form.pendingGoogleFile.name })
                          : detail?.has_credentials
                            ? t('sso.serviceAccountConfigured', 'Service account configured.')
                            : t('sso.noServiceAccountNotice', 'No service account configured yet. Click Import to upload, paste, or type the service-account JSON.')}
                      </Text>
                    </VStack>
                  ) : (
                    <CredentialInput
                      hasCredentials={detail?.has_credentials ?? false}
                      credentialShape={credentialShapeFor(form.idp_type)}
                      rawIdpCredentialsText={form.rawIdpCredentialsText}
                      onIdpCredentialsTextChange={(v) =>
                        setForm((f) => ({ ...f, rawIdpCredentialsText: v }))
                      }
                    />
                  )}

                  {/* SCIM endpoint URL — the URL push IdPs (Okta, Azure)
                  POST users to. Pull IdPs (Google, Duo) don't push, so
                  they don't need it. */}
                  {isPushIdp(form.idp_type) && typeof window !== 'undefined' ? (
                    <Box mt={5}>
                      <CopyableRow
                        label={t('sso.scimEndpointUrl', 'SCIM endpoint URL')}
                        value={`${window.location.origin}/api/v2/scim/v2/`}
                        fieldId="scim-base-url"
                      />
                    </Box>
                  ) : null}

                  {/* Webhook signing secret is Okta-only — the only inbound
                  webhook handler is OktaEventHookView, which compares
                  the Authorization Bearer against config.webhook_secret
                  (scim/webhooks.py:91). Azure / Duo / Google have no
                  inbound webhook flow, so the field doesn't apply. */}
                  {form.idp_type === 'okta' ? (
                    <Field.Root mt={5}>
                      <Field.Label htmlFor="sso-webhook-secret">
                        {t('sso.webhookSigningSecret', 'Webhook signing secret')}
                      </Field.Label>
                      <InputField
                        id="sso-webhook-secret"
                        type="password"
                        placeholder={
                          detail?.has_webhook_secret
                            ? '••••••••••••'
                            : t('sso.pasteNewSecret', 'Paste new secret')
                        }
                        value={form.rawWebhookSecret}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            rawWebhookSecret: e.target.value,
                          }))
                        }
                      />
                    </Field.Root>
                  ) : null}
                </SectionCard>

                {!isCreate ? (
                  <SectionCard
                    title={t('sso.status', 'Status')}
                    action={
                      isPullIdp(form.idp_type) ? (
                        <HStack gap={2}>
                          <chakra.label
                            htmlFor="sso-polling-interval"
                            fontSize="sm"
                            color="text.secondary"
                          >
                            {t('sso.pollsEvery', 'Polls every')}
                          </chakra.label>
                          <InputField
                            id="sso-polling-interval"
                            type="number"
                            w="64px"
                            size="sm"
                            min={1}
                            max={1440}
                            value={form.polling_interval_minutes}
                            onChange={(e) =>
                              setForm((f) => ({
                                ...f,
                                polling_interval_minutes:
                                  Number(e.target.value) || 5,
                              }))
                            }
                            data-e2e="sso-polling-interval"
                          />
                          <Text fontSize="sm" color="text.secondary">
                            {t('sso.min', 'min')}
                          </Text>
                        </HStack>
                      ) : undefined
                    }
                  >
                    <StatusPanel
                      status={statusQuery.data}
                      isLoading={statusQuery.isLoading}
                    />
                  </SectionCard>
                ) : null}
              </VStack>
            </form>
          </FormProvider>
        )}
      </DrawerComponent>

      {!isCreate && configId !== null ? (
        <BearerRotateModal
          configId={configId}
          open={bearerModalOpen}
          onClose={() => setBearerModalOpen(false)}
          onRotated={() => {
            queryClient.invalidateQueries({
              queryKey: ['sso', 'scim-configs', configId],
            });
          }}
        />
      ) : null}

      <BearerRevealModal
        token={revealedBearer}
        variant="new"
        onClose={() => {
          setRevealedBearer(null);
          onClose();
        }}
      />

      {form.idp_type === 'google' ? (
        <GoogleCredsImportDialog
          isOpen={googleImportOpen}
          configId={isCreate ? null : configId}
          hasCredentials={detail?.has_credentials ?? false}
          adminEmail={form.adminEmail}
          onClose={() => setGoogleImportOpen(false)}
          onStaged={(file) => {
            setForm((f) => ({ ...f, pendingGoogleFile: file }));
            setGoogleImportOpen(false);
          }}
          onUploaded={() => {
            if (configId !== null) {
              queryClient.invalidateQueries({
                queryKey: ['sso', 'scim-configs', configId],
              });
            }
          }}
        />
      ) : null}
    </>
  );
}

export default ScimConfigDrawer;
